import { useMemo, useState } from 'react'

import { ShowMoreToggle } from '~/components/ShowMoreToggle'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { TokenSelectorOption } from '~/components/smart-accounts/TokenSelectorOption'
import { CliffPill } from '~/components/smart-accounts/vesting/CliffPill'
import { VestingDropDownContent } from '~/components/smart-accounts/vesting/VestingDropDownContent'
import { useVestingBalances } from '~/components/smart-accounts/vesting/hooks/useVestingBalances'
import {
  VestingBalance,
  VestingProtocol,
} from '~/components/smart-accounts/vesting/types'
import { useOrgSmartAccount } from '~/hooks/useSmartAccounts'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

const MAX_DISPLAYED_BALANCES = 3

export const VestingBalances = ({ address }: { address: IAddress }) => {
  const [showAll, setShowAll] = useState(false)

  const { convertTokenToUSD } = useTokenPrices()

  const { smartAccount } = useOrgSmartAccount(address)
  const { data: vestingBalances } = useVestingBalances(
    smartAccount?.id,
    smartAccount?.orgId,
  )

  const showVestingBalances =
    vestingBalances && vestingBalances.length > 0 && smartAccount?.address

  const displayedBalances = useMemo(() => {
    if (!vestingBalances) {
      return []
    }

    return vestingBalances
      .map((balance) => ({
        ...balance,
        usdValue: convertTokenToUSD({
          token: balance.tokenAddress,
          amount: balance.claimableAmount,
          chainId: balance.chainId,
        }),
      }))
      .sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0))
  }, [vestingBalances, convertTokenToUSD])

  return showVestingBalances ? (
    <div className="-mx-3 flex flex-col">
      {displayedBalances
        .slice(0, showAll ? displayedBalances.length : MAX_DISPLAYED_BALANCES)
        .map((balance) => (
          <VestingBalanceToken key={balance.id} balance={balance} />
        ))}
      {displayedBalances.length > MAX_DISPLAYED_BALANCES && (
        <div className="flex justify-center pt-2">
          <ShowMoreToggle
            isExpanded={showAll}
            onToggle={() => setShowAll(!showAll)}
            expandedLabel="View less"
            collapsedLabel={`View ${displayedBalances.length - MAX_DISPLAYED_BALANCES} more`}
          />
        </div>
      )}
    </div>
  ) : null
}

const VestingBalanceToken = ({ balance }: { balance: VestingBalance }) => {
  const cliffNotReached = useMemo(
    () =>
      balance.cliffTime !== undefined &&
      new Date().getTime() < balance.cliffTime * 1000,
    [balance.cliffTime],
  )

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (isOpen) {
          sendTrackingData('vestingDropdownOpened', {
            tokenAddress: balance.tokenAddress,
            chainId: balance.chainId,
            protocol: balance.protocol,
          })
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <div className="rounded-lg data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800">
          <TokenSelectorOption
            token={balance.tokenAddress}
            chainId={balance.chainId}
            balance={balance.claimableAmount}
            protocol={getProtocolLabel(balance.protocol)}
            className="transition hover:cursor-pointer md:hover:bg-gray-100 md:dark:hover:bg-gray-800"
            suffix={
              cliffNotReached ? (
                <CliffPill cliffTime={balance.cliffTime} />
              ) : undefined
            }
          />
        </div>
      </DropdownMenuTrigger>
      <VestingDropDownContent vestingBalance={balance} />
    </DropdownMenu>
  )
}
export const getProtocolLabel = (protocol: VestingProtocol) => {
  switch (protocol) {
    case VestingProtocol.SablierV3:
      return 'Sablier'
    case VestingProtocol.SablierV2:
      return 'Sablier'
    case VestingProtocol.Hedgey:
      return 'Hedgey'
    case VestingProtocol.HedgeyTokenLockup:
      return 'Hedgey'
  }
}
